import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPublicDriverStats } from "../../lib/publicDbApi";

function flagEmoji(code) {
    const c = String(code ?? "").trim().toUpperCase();
    if (c.length !== 2) return "🏁";
    return c
        .split("")
        .map((ch) => String.fromCodePoint(127397 + ch.charCodeAt()))
        .join("");
}

function asNumber(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function posLabel(v) {
    const n = asNumber(v, 0);
    return n > 0 ? `P${n}` : "DNF";
}

function getDeltaClass(delta) {
    if (delta > 0) return "is-up";
    if (delta < 0) return "is-down";
    return "is-neutral";
}

function positionToHeight(pos) {
    const p = Math.max(1, Math.min(asNumber(pos, 12), 12));
    const inverted = 13 - p;
    return 42 + inverted * 4.2;
}
function getClassVariant(className) {
    const c = String(className ?? "").trim().toUpperCase();
    if (c === "PRO") return "pro";
    if (c === "INT") return "int";
    if (c === "AM") return "am";
    return "default";
}

function getLastNonDnsClassFinish(historyRows) {
    const vals = historyRows
        .map((r) => asNumber(r.class_pos, 0))
        .filter((v) => v > 0);

    if (!vals.length) return "–";
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}
function getFormAccent(pos) {
    const p = asNumber(pos, 99);
    if (p <= 3) return "yellow";
    if (p <= 6) return "green";
    return "gray";
}

function calcConsistency(historyRows) {
    const vals = historyRows
        .map((r) => asNumber(r.class_pos, 0))
        .filter((v) => v > 0);

    if (!vals.length) return 0;

    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    const variance = vals.reduce((sum, v) => sum + (v - avg) ** 2, 0) / vals.length;
    const std = Math.sqrt(variance);
    return Math.max(0, Math.min(100, Math.round(100 - std * 12)));
}

function calcAvg(historyRows, field) {
    const vals = historyRows
        .map((r) => asNumber(r[field], 0))
        .filter((v) => v > 0);

    if (!vals.length) return "–";
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
}

function StatCard({ label, value, accentValue = false, delta = null }) {
    return (
        <div className={`rv-stat-card ${accentValue ? "rv-stat-card--highlight" : ""}`}>
            <div className="rv-stat-card__label">{label}</div>
            <div className="rv-stat-card__valueRow">
                <div className={`rv-stat-card__value ${accentValue ? "rv-stat-card__value--accent" : ""}`}>
                    {value}
                </div>

                {delta !== null && delta !== undefined && (
                    <div className={`rv-stat-card__delta ${getDeltaClass(delta)}`}>
                        {delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {delta > 0 ? `+${delta}` : delta}
                    </div>
                )}
            </div>
        </div>
    );
}

function LineChartCard({ title, data, invert = false, valueType = "number" }) {
    const validData = data.filter((d) => {
        if (d.value === null || d.value === undefined || d.value === "") return false;
        const n = Number(d.value);
        if (!Number.isFinite(n)) return false;
        if (valueType === "position" && n <= 0) return false;
        return true;
    });

    if (!validData.length) {
        return (
            <div className="rv-panel rv-panel--chart">
                <div className="rv-panel__title">{title}</div>
                <div className="rv-empty">Keine Daten vorhanden.</div>
            </div>
        );
    }

    const values = validData.map((d) => Number(d.value));

    let paddedMin;
    let paddedMax;
    let yLabels;

    if (valueType === "position") {
        paddedMin = 1;

        const rawMax = Math.max(...values, 5);
        paddedMax = Math.ceil(rawMax / 5) * 5;

        const tickCount = 5;
        yLabels = Array.from({ length: tickCount }, (_, i) => {
            const ratio = i / (tickCount - 1);
            return Math.round(paddedMin + ratio * (paddedMax - paddedMin));
        });

        if (!yLabels.includes(1)) {
            yLabels.unshift(1);
        }

        yLabels = [...new Set(yLabels)].sort((a, b) => a - b);
    } else {
        const max = Math.max(...values, 1);
        const min = Math.min(...values, 0);

        paddedMax = Math.ceil((max + 5) / 10) * 10;
        paddedMin = Math.max(0, Math.floor(min / 10) * 10);

        const yTickCount = 5;
        yLabels = Array.from({ length: yTickCount }, (_, i) => {
            const ratio = i / (yTickCount - 1);
            return Math.round(paddedMax - ratio * (paddedMax - paddedMin));
        });
    }

    const range = Math.max(paddedMax - paddedMin, 1);
    const xStep = 100 / Math.max(data.length, 1);

    const chartPoints = data
        .map((item, index) => {
            if (item.value === null || item.value === undefined || item.value === "") return null;

            const numValue = Number(item.value);
            if (!Number.isFinite(numValue)) return null;
            if (valueType === "position" && numValue <= 0) return null;

            const x = index * xStep + xStep / 2;
            const normalized = (numValue - paddedMin) / range;

            const y =
                valueType === "position"
                    ? 8 + normalized * 72
                    : invert
                        ? 8 + normalized * 72
                        : 80 - normalized * 72;

            return {
                x,
                y,
                value: numValue,
                label: item.label,
                index,
            };
        })
        .filter(Boolean);

    const polylinePoints = chartPoints.map((p) => `${p.x},${p.y}`).join(" ");
    const last = chartPoints[chartPoints.length - 1];

    const formatValue = (value) => {
        if (valueType === "position") return `P${value}`;
        return value;
    };

    return (
        <div className="rv-panel rv-panel--chart">
            {title ? <div className="rv-panel__title">{title}</div> : null}

            <div className="rv-lineChart rv-lineChart--withAxis">
                <div className="rv-lineChart__yAxis">
                    {yLabels.map((label) => (
                        <span key={label}>
                            {valueType === "position" ? `P${label}` : label}
                        </span>
                    ))}
                </div>

                <div className="rv-lineChart__plot">
                    <div className="rv-lineChart__grid rv-lineChart__grid--y">
                        {yLabels.map((label, i) => (
                            <div
                                key={`h-${label}-${i}`}
                                className="rv-lineChart__gridLine rv-lineChart__gridLine--h"
                            />
                        ))}
                    </div>

                    <div
                        className="rv-lineChart__grid rv-lineChart__grid--x"
                        style={{ gridTemplateColumns: `repeat(${data.length}, 1fr)` }}
                    >
                        {data.map((item, i) => (
                            <div
                                key={`v-${item.label}-${i}`}
                                className="rv-lineChart__gridLine rv-lineChart__gridLine--v"
                            />
                        ))}
                    </div>

                    <svg viewBox="0 0 100 88" preserveAspectRatio="none" className="rv-lineChart__svg">
                        <polyline className="rv-lineChart__pathGlow" fill="none" points={polylinePoints} />
                        <polyline className="rv-lineChart__path" fill="none" points={polylinePoints} />

                        {chartPoints.map((point, index) => {
                            const isLast = index === chartPoints.length - 1;
                            return (
                                <circle
                                    key={`${point.label}-${index}`}
                                    cx={point.x}
                                    cy={point.y}
                                    r={isLast ? 1.9 : 1.45}
                                    className={isLast ? "rv-lineChart__dot rv-lineChart__dot--last" : "rv-lineChart__dot"}
                                />
                            );
                        })}
                    </svg>

                    {last && (
                        <div className="rv-lineChart__badge">
                            {formatValue(last.value)}
                        </div>
                    )}

                    <div
                        className="rv-lineChart__labels"
                        style={{ gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, 1fr)` }}
                    >
                        {data.map((item) => (
                            <span key={item.label}>{item.label}</span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
function FormCurveCard({ items }) {
    if (!items.length) {
        return (
            <div className="rv-panel rv-panel--chart">
                <div className="rv-panel__title">Formkurve (letzte 5 Rennen - class)</div>
                <div className="rv-empty">Keine Daten vorhanden.</div>
            </div>
        );
    }

    const bestPos = Math.min(...items.map((i) => i.pos));

    return (
        <div className="rv-panel rv-panel--chart">
            <div className="rv-panel__title">Formkurve (letzte 5 Rennen - Class)</div>
            <div
                className="rv-formChart"
                style={{ gridTemplateColumns: `repeat(${Math.max(items.length, 1)}, 1fr)` }}
            >
                {items.map((item, index) => {
                    const accent = getFormAccent(item.pos);
                    const isActive =
                        item.pos === bestPos &&
                        index === items.findIndex((x) => x.pos === bestPos);

                    return (
                        <div key={`${item.raceTag}-${index}`} className="rv-formChart__col">
                            <div
                                className={`rv-formBar rv-formBar--${accent} ${isActive ? "rv-formBar--active" : ""}`}
                                style={{ height: `${positionToHeight(item.pos)}%` }}
                                title={`${item.raceTag}: ${item.label}`}
                            >
                                <span className="rv-formBar__label">{item.label}</span>
                                <span className="rv-formBar__line" />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function MiniStatsGrid({ driver, history }) {
    const starts = asNumber(driver.starts, history.length);
    const incidentTotal = asNumber(driver.incident_total, 0);
    const licensePoints = asNumber(driver.license_points, 0);
    const lapsDriven = asNumber(driver.laps_driven, 0);
    const kmDrivenTotalRaw = driver.km_driven_total ?? "";
    const kmDrivenTotalNum = Number(String(kmDrivenTotalRaw).replace(",", "."));
    const kmDrivenTotal = Number.isFinite(kmDrivenTotalNum)
        ? kmDrivenTotalNum.toFixed(1)
        : (kmDrivenTotalRaw || "–");
    const dnf = asNumber(driver.dnf, 0);

    const licenseMax = 18;
    const licensePercent = clamp((licensePoints / licenseMax) * 100, 0, 100);

    const items = [
        { label: "Starts", value: starts },
        { label: "Incidents Gesamt", value: incidentTotal },
        {
            label: "Strafpunkte",
            value: `${licensePoints}`,
            progress: true,
            progressPercent: licensePercent,
            progressText: `${licensePoints} / ${licenseMax}`,
        },
        { label: "Laps Driven", value: lapsDriven },
        { label: "Kilometer gefahren", value: kmDrivenTotal },
        { label: "DNF", value: dnf },
    ];

    return (
        <div className="rv-panel">
            <div className="rv-panel__title">Rennstatistiken</div>
            <div className="rv-miniStats">
                {items.map((item) => (
                    <div key={item.label} className="rv-miniStats__card">
                        {item.progress ? (
                            <>
                                <div
                                    className="rv-miniStats__progressBar"
                                    style={{ width: `${item.progressPercent}%` }}
                                />
                                <div className="rv-miniStats__content">
                                    <div className="rv-miniStats__label">{item.label}</div>
                                    <div className="rv-miniStats__value">{item.value}</div>
                                    <div className="rv-miniStats__subvalue">{item.progressText}</div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="rv-miniStats__label">{item.label}</div>
                                <div className="rv-miniStats__value">{item.value}</div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function ResultsTable({ rows }) {
    return (
        <div className="rv-panel">
            <div className="rv-panel__title">Letzte Rennen</div>

            {!rows.length ? (
                <div className="rv-empty">Keine Daten vorhanden.</div>
            ) : (
                <div className="rv-resultsTableWrap">
                    <table className="rv-resultsTable">
                        <thead>
                        <tr>
                            <th>Runde</th>
                            <th>Land</th>
                            <th>Strecke</th>
                            <th>Qualy Class</th>
                            <th>Start Overall</th>
                            <th>Finish Class</th>
                            <th>Punkte Class</th>
                            <th>Inc</th>
                            <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((r) => (
                            <tr key={`${r.driver_id}-${r.race_tag}`}>
                                <td>{r.race_tag}</td>
                                <td>
                                    {r.event_flag_url ? (
                                        <img
                                            src={r.event_flag_url}
                                            alt={r.event_country_code || "Flag"}
                                            className="rv-resultsTable__flag"
                                        />
                                    ) : r.event_country_code ? (
                                        <span className="rv-resultsTable__flagEmoji">
                                            {flagEmoji(r.event_country_code)}
                                        </span>
                                    ) : (
                                        "–"
                                    )}
                                </td>
                                <td>{r.track_name || "–"}</td>
                                <td>{r.quali_pos_class != null && r.quali_pos_class !== "" ? `P${r.quali_pos_class}` : "–"}</td>
                                <td>{r.start_all_pos != null && r.start_all_pos !== "" ? `P${r.start_all_pos}` : "–"}</td>
                                <td>{r.class_pos != null && r.class_pos !== "" ? `P${r.class_pos}` : "–"}</td>
                                <td>{r.points_class != null && r.points_class !== "" ? r.points_class : "–"}</td>
                                <td>{r.incidents != null && r.incidents !== "" ? r.incidents : "–"}</td>
                                <td>{r.finish_status || "–"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function DriverProfilePage({ driverSlug, seasonKey = "active" }) {
    const navigate = useNavigate();

    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                setError("");

                const data = await fetchPublicDriverStats({
                    seriesKey: "gt3x",
                    seasonKey,
                    driverSlug,
                });

                if (!cancelled) {
                    setProfileData(data);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message || "Fehler beim Laden des Fahrerprofils.");
                    setProfileData(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [driverSlug, seasonKey]);

    const driver = useMemo(() => {
        return profileData?.driver || null;
    }, [profileData]);

    const driverHistory = useMemo(() => {
        return [...(profileData?.history || [])].sort(
            (a, b) => asNumber(a.round_number, 0) - asNumber(b.round_number, 0)
        );
    }, [profileData]);

    const classVariant = useMemo(() => getClassVariant(driver?.class), [driver?.class]);

    const racesCount = useMemo(() => {
        if (driver?.starts !== undefined && driver?.starts !== "") {
            return asNumber(driver.starts, 0);
        }
        return driverHistory.filter(
            (r) => String(r.dns ?? "").trim().toUpperCase() !== "TRUE"
        ).length;
    }, [driver, driverHistory]);

    const avgFinishClass = useMemo(() => {
        if (driver?.avg_race_pos_class !== undefined && driver?.avg_race_pos_class !== "") {
            return driver.avg_race_pos_class;
        }
        return getLastNonDnsClassFinish(driverHistory);
    }, [driver, driverHistory]);

    const pointsSeries = useMemo(() => {
        const sortedRows = [...driverHistory].sort(
            (a, b) => asNumber(a.round_number, 0) - asNumber(b.round_number, 0)
        );

        const byRound = new Map();
        let fallbackRunningTotal = 0;

        sortedRows.forEach((r) => {
            const round = asNumber(r.round_number, 0);
            if (round <= 0) return;

            const classTotal = asNumber(r.points_total_after_race_class, NaN);

            if (Number.isFinite(classTotal)) {
                byRound.set(round, classTotal);
                fallbackRunningTotal = classTotal;
                return;
            }

            const racePointsRaw = String(r.points_class ?? "").trim().toUpperCase();
            const racePoints =
                racePointsRaw === "DNF" || racePointsRaw === "DNS" || racePointsRaw === "DSQ"
                    ? 0
                    : asNumber(r.points_class, 0);

            fallbackRunningTotal += racePoints;
            byRound.set(round, fallbackRunningTotal);
        });

        return Array.from({ length: 12 }, (_, i) => {
            const round = i + 1;

            return {
                label: `R${round}`,
                value: byRound.has(round) ? byRound.get(round) : null,
            };
        });
    }, [driverHistory]);

    const qualySeries = useMemo(() => {
        return driverHistory.map((r) => {
            const value = asNumber(r.quali_pos_class, 0);

            return {
                label: r.race_tag,
                value: value > 0 ? value : null,
            };
        });
    }, [driverHistory]);

    const formData = useMemo(() => {
        return driverHistory.slice(-5).map((r) => ({
            raceTag: r.race_tag,
            label:
                String(r.dnf ?? "").trim().toUpperCase() === "TRUE"
                    ? "DNF"
                    : String(r.dns ?? "").trim().toUpperCase() === "TRUE"
                        ? "DNS"
                        : posLabel(r.class_pos),
            pos: asNumber(r.class_pos, 99),
        }));
    }, [driverHistory]);

    const latestResults = useMemo(() => {
        return [...driverHistory].slice(-5).reverse();
    }, [driverHistory]);

    const avgStart = useMemo(() => calcAvg(driverHistory, "quali_pos_class"), [driverHistory]);

    const positionsGainedTotal = useMemo(() => {
        if (driver?.positions_gained_total !== undefined && driver?.positions_gained_total !== "") {
            return asNumber(driver.positions_gained_total, 0);
        }
        return driverHistory.reduce((sum, r) => sum + asNumber(r.positions_gained, 0), 0);
    }, [driver, driverHistory]);

    const consistencyScore = useMemo(() => {
        if (driver?.consistency_score !== undefined && driver?.consistency_score !== "") {
            return asNumber(driver.consistency_score, 0);
        }
        return calcConsistency(driverHistory);
    }, [driver, driverHistory]);

    if (loading) {
        return (
            <div className="rv-driverPage">
                <div className="rv-driverPage__state">Lade Fahrerprofil …</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rv-driverPage">
                <div className="rv-driverPage__state rv-driverPage__state--error">{error}</div>
            </div>
        );
    }

    if (!driver) {
        return (
            <div className="rv-driverPage">
                <div className="rv-driverPage__state rv-driverPage__state--error">
                    Fahrer nicht gefunden.
                </div>
            </div>
        );
    }

    return (
        <div className="rv-driverPage">
            <div className="rv-driverPage__inner">
                <div className="rv-driverPage__main">
                    <div className="rv-breadcrumb">
                        <button
                            type="button"
                            className="rv-breadcrumb__back"
                            onClick={() => navigate(-1)}
                        >
                            Fahrer
                        </button>
                        <span>›</span>
                        {String(driver.driver_name || "").toUpperCase()}
                    </div>

                    <section className="rv-hero">
                        <div className="rv-hero__profile">
                            <div className="rv-hero__avatarWrap">
                                {driver.driver_image_url || driver.discord_avatar_url ? (
                                    <img
                                        className="rv-hero__avatar"
                                        src={driver.driver_image_url || driver.discord_avatar_url}
                                        alt={driver.driver_name}
                                    />
                                ) : (
                                    <div className="rv-hero__avatar rv-hero__avatarFallback">
                                        {driver.avatar_initials || "?"}
                                    </div>
                                )}
                            </div>

                            <div className="rv-hero__info">
                                <div className="rv-hero__titleRow">
                                    <h1>{driver.driver_name}</h1>

                                    <div className="rv-hero__badges">
                    <span className="rv-badge rv-badge--flag">
                        {driver.flag_url ? (
                            <img
                                className="rv-badge__flagImg"
                                src={driver.flag_url}
                                alt={driver.country_code || "Flag"}
                            />
                        ) : (
                            flagEmoji(driver.country_code)
                        )}
                    </span>

                                        <span className={`rv-badge rv-badge--number rv-badge--number-${classVariant}`}>
                        #{driver.car_number || "–"}
                    </span>
                                    </div>
                                </div>

                                <div className="rv-hero__teamRow">
                                    <span className="rv-hero__teamLabel">Team:</span>
                                    <span className="rv-hero__teamName">{driver.team_name || "–"}</span>
                                </div>

                                <div className="rv-hero__chips">
                <span className={`rv-chip rv-chip--class rv-chip--class-${classVariant}`}>
                    {driver.class || "KLASSE"}
                </span>
                                    <span className="rv-chip">{driver.car_name || "Fahrzeug"}</span>
                                    <span className="rv-chip">{driver.status || "AKTIV"}</span>
                                </div>
                            </div>
                        </div>

                        <div className="rv-topStats">
                            <StatCard label="Punkte" value={driver.current_points_class ?? 0} />
                            <StatCard
                                label="Position Class"
                                value={`${driver.current_position_class ?? "–"}.`}
                                accentValue
                                delta={asNumber(driver.position_delta_class, 0)}
                            />
                            <StatCard label="Races" value={racesCount} />
                            <StatCard label="AVG Finish Class" value={avgFinishClass} />
                        </div>
                    </section>

                    <div className="rv-tabs">
                        <button className="is-active">Übersicht</button>
                        <button>Statistiken</button>
                        <button>Rennverlauf</button>
                        <button>Qualifying</button>
                        <button>Incidents</button>
                        <button>Team</button>
                        <button>Vergleiche</button>
                    </div>

                    <div className="rv-gridTop">
                        <LineChartCard
                            title={driver.class ? `Punkteentwicklung - ${driver.class}` : "Punkteentwicklung"}
                            data={pointsSeries}
                        />
                        <FormCurveCard items={formData} />
                    </div>

                    <div className="rv-gridBottom">
                        <MiniStatsGrid driver={driver} history={driverHistory} />

                        <div className="rv-panel">
                            <div className="rv-panel__header">
                                <div className="rv-panel__title">Qualifying-Verlauf (class)</div>
                                <div className="rv-panel__meta">
                                    Ø Qualy Class: <strong>{avgStart}</strong>
                                </div>
                            </div>
                            <LineChartCard title="" data={qualySeries} valueType="position" />
                        </div>

                        <div className="rv-stackCol">
                            <div className="rv-panel rv-panel--info">
                                <div className="rv-panel__title">Positionsgewinne</div>
                                <div className="rv-infoNumber">
                                    {positionsGainedTotal >= 0 ? `+${positionsGainedTotal}` : positionsGainedTotal}
                                </div>
                                <div className="rv-infoSub">Plätze gesamt</div>
                            </div>

                            <div className="rv-panel rv-panel--info">
                                <div className="rv-panel__title">Konstanz</div>
                                <div className="rv-infoNumber">{consistencyScore}%</div>
                                <div className="rv-infoSub">über alle Rennresultate</div>
                            </div>
                        </div>
                    </div>

                    <div className="rv-gridBottomSingle">
                        <ResultsTable rows={latestResults} />
                    </div>
                </div>
            </div>
        </div>
    );
}